import React, {useMemo} from 'react'
import {Col, Container, Row} from 'styled-bootstrap-grid'

import type {
  CategoriesQuery,
  CategoriesQueryVariables,
} from 'src/api/saleor/generated'
import {
  CategoriesDocument,
  useNavigationCategoriesQuery,
  NavigationCategoriesDocument,
} from 'src/api/saleor/generated'
import {serverPropsRender} from 'src/utils/serverRender/serverPropsRender'

import PageContainer from 'src/components/layout/PageContainer'
import CategoryCard from 'src/components/category/CategoryCard'
import {SpacingWrapper} from 'src/components/layout'
import {H1, H2} from 'src/components/typography'
import {makeSubscriptionProps} from 'src/api/datocms/utils'
import {pageQuery} from 'src/api/datocms/queries'
import {Breadcrumbs} from 'src/components/products'
import {paths} from 'src/utils/paths'
import {useQuery} from '@apollo/client'
import {PageLoading} from 'src/components/genericPage'
import {isSSR} from 'src/utils/isSSR'
import {isNotNull} from 'src/utils/isNotNull'

export const getServerSideProps = serverPropsRender({
  query: async ({query}) => {
    await query({
      query: NavigationCategoriesDocument,
    })
    return query({
      query: CategoriesDocument,
      variables: {
        first: 100,
        level: 0,
      },
    })
  },
  generateInitProps: async ({context}) => ({
    pageSubscription: await makeSubscriptionProps(
      !!context.preview,
      pageQuery,
      false,
    ),
  }),
})

const DONOT_SHOW_SLUGS = ['baeklingar-sreh', 'akstur-sref']
const DEFAULT_IMAGE = '/assets/voruspjald.png'

export default function Voruflokkar(): JSX.Element {
  const {data, error, loading} = useNavigationCategoriesQuery()
  const {
    data: categoriesData,
    error: categoriesError,
    loading: categoriesLoading,
  } = useQuery<CategoriesQuery, CategoriesQueryVariables>(CategoriesDocument, {
    variables: {first: 100, level: 0},
  })

  const countObj: Record<string, number> = useMemo(() => {
    if (categoriesError || categoriesLoading || !categoriesData?.categories) {
      return {}
    }
    return categoriesData.categories.edges.filter(isNotNull).reduce(
      (a, b) => ({
        ...a,
        [b.node.slug]: b.node.products?.totalCount ?? 0,
      }),
      {} as Record<string, number>,
    )
  }, [categoriesData?.categories, categoriesError, categoriesLoading])
  if (error) {
    // TODO: Better error handling
    return (
      <PageContainer title="Villa">
        <Container>
          <H2>Villa</H2>
        </Container>
      </PageContainer>
    )
  }

  if (!isSSR && loading) {
    return <PageLoading />
  }

  return (
    <PageContainer title="Allir vöruflokkar | Rekstrarvörur">
      <Container>
        <Breadcrumbs
          breadcrumbs={[
            {title: 'Forsíða', href: paths.home},
            {title: 'Vefverslun', href: paths.categories},
          ]}
        />
        <SpacingWrapper spacing={32}>
          <H1 withGutter>Vefverslun</H1>
          <Row>
            {(data?.categories?.edges || [])
              .filter((e) => !DONOT_SHOW_SLUGS.includes(e.node.slug))
              .map(({node}) => (
                <Col key={node.id} md={3}>
                  <CategoryCard
                    title={node.name}
                    href={`${paths.categories}/${node.slug}`}
                    src={node.backgroundImage?.url || DEFAULT_IMAGE}
                    description={
                      countObj[node.slug] ? `${countObj[node.slug]} vörur` : ''
                    }
                  />
                </Col>
              ))}
          </Row>
        </SpacingWrapper>
      </Container>
    </PageContainer>
  )
}
